// EnvironmentDirectUpload — no-wizard direct uploader for gsplat files.
//
// ENG-529: dropping a .ply / .splat / .spz / .ksplat into the content browser
// uploads immediately with sensible defaults (name = filename stem, default
// pose = identity, folder_path = drop target). This component runs the upload
// on mount and closes itself when done — there's no form to fill out.
//
// Shows real upload progress via XHR's upload-progress event (fetch() can't
// do this). On error, the modal stays up with the message so the user can
// dismiss it deliberately (loud failure per CLAUDE.md UX rules).

import React, { useEffect, useRef, useState } from 'react'
import WizardShell, { tokens } from './WizardShell'

// Progress-bar fill. The accent is the palette's, so the bar reads as part of
// the UI in either theme; the dim stop is the same hue pulled toward the track
// so the gradient still has a direction.
const FILL = 'var(--m-accent)'
const FILL_DIM = 'color-mix(in srgb, var(--m-accent) 55%, var(--m-bg))'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '')
}

function fmtSize(bytes) {
  if (bytes == null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = Number(bytes), i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}

export default function EnvironmentDirectUpload({ file, path = [], onDone, onCancel }) {
  const [error, setError] = useState(null)
  // fraction ∈ [0, 1] or -1 for indeterminate; null = upload finished, waiting on server response
  const [progress, setProgress] = useState({ fraction: 0, loaded: 0, total: file?.size ?? 0 })
  const [finalizing, setFinalizing] = useState(false)
  const fired = useRef(false)

  useEffect(() => {
    if (!file || fired.current) return
    fired.current = true
    const folderPath = path.length ? '/' + path.join('/') : '/environments'
    CustomAssetManager.uploadEnvironmentAsset(file, {
      name: baseName(file),
      folderPath,
      onProgress: (p) => {
        setProgress(p)
        // When XHR.upload reaches 100%, the request body has been sent but
        // we're still waiting on the server to write to S3 and INSERT the
        // row — show a "finalizing" state instead of a frozen 100% bar.
        if (p.fraction >= 1) setFinalizing(true)
      },
    })
      .then(row => {
        assetRegistry.setPath(`environment:${row.id}`, path)
        onDone?.(row)
      })
      .catch(e => {
        setError(String(e?.message ?? e))
      })
  }, [file, path, onDone])

  const pct = progress.fraction >= 0 ? Math.round(progress.fraction * 100) : null

  return (
    <WizardShell
      title={error ? 'Environment upload failed' : (finalizing ? 'Finalizing upload…' : 'Uploading environment…')}
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={onCancel}
      submitDisabled={!error}
      submitLabel={error ? 'Close' : (finalizing ? 'Finalizing…' : (pct != null ? `${pct}%` : 'Uploading…'))}
      error={error}
    >
      {!error && (
        <div style={{ padding: '8px 0 4px' }}>
          {/* Progress bar — determinate when we have total bytes, indeterminate
              stripe when not. Server-side processing after 100% upload is
              labelled "Finalizing" since XHR can't see it. */}
          <div style={{
            width: '100%', height: 10,
            background: tokens.bgInset,
            border: `1px solid ${tokens.border}`,
            borderRadius: 4,
            overflow: 'hidden',
            position: 'relative',
          }}>
            {progress.fraction >= 0 ? (
              <div style={{
                width: `${Math.max(2, (progress.fraction || 0) * 100)}%`,
                height: '100%',
                background: finalizing
                  ? `linear-gradient(90deg, ${FILL_DIM}, ${FILL}, ${FILL_DIM})`
                  : `linear-gradient(90deg, ${FILL_DIM}, ${FILL})`,
                backgroundSize: finalizing ? '200% 100%' : 'auto',
                animation: finalizing ? 'aos-env-upload-pulse 1.4s linear infinite' : 'none',
                transition: 'width 0.15s linear',
              }} />
            ) : (
              <div style={{
                position: 'absolute', inset: 0,
                background: `linear-gradient(90deg, transparent 0%, ${FILL} 50%, transparent 100%)`,
                backgroundSize: '40% 100%',
                animation: 'aos-env-upload-stripe 1.2s linear infinite',
              }} />
            )}
          </div>

          <div style={{
            marginTop: 8, color: tokens.dim, fontSize: 11,
            display: 'flex', justifyContent: 'space-between', gap: 8,
          }}>
            <span>
              {finalizing
                ? 'Server writing to storage…'
                : (pct != null ? `${pct}%` : 'uploading…')}
            </span>
            <span>
              {progress.total > 0
                ? `${fmtSize(progress.loaded)} / ${fmtSize(progress.total)}`
                : (progress.loaded > 0 ? fmtSize(progress.loaded) : fmtSize(file?.size))}
            </span>
          </div>

          <div style={{ marginTop: 10, color: tokens.faint, fontSize: 10 }}>
            Large gsplat files can take a while — please don't close this tab.
          </div>
        </div>
      )}
      <style>{`
        @keyframes aos-env-upload-stripe { from { background-position: -40% 0; } to { background-position: 140% 0; } }
        @keyframes aos-env-upload-pulse  { from { background-position: 0 0; } to { background-position: 200% 0; } }
      `}</style>
    </WizardShell>
  )
}
